import type {
  MediaAssetDto,
  MediaPostDto,
  MediaPostStatus,
  Paged,
} from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { scopeQuery, useScope } from '../lib/scope';
import { ScopedEmptyState } from '../components/ScopedEmptyState';
import {
  MEDIA_CHANNEL_LABELS,
  MEDIA_KIND_LABELS,
  MEDIA_STATUS_LABELS,
  formatDate,
  fromDateInput,
  toDateInput,
} from '../lib/format';
import { OrgUnitPicker } from '../components/OrgUnitPicker';
import { PageHeader } from '../components/PageHeader';
import { PersonSelect } from '../components/PersonPicker';
import { Pagination } from '../components/Pagination';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Checkbox,
  Dialog,
  ErrorBlock,
  Field,
  Input,
  LoadingBlock,
  Select,
  Tabs,
  Textarea,
  cn,
  useToast,
} from '../components/ui';

const NEXT_STATUS: Partial<Record<MediaPostStatus, { target: MediaPostStatus; label: string }[]>> = {
  IDEA: [{ target: 'DRAFT', label: 'In Entwurf' }],
  DRAFT: [{ target: 'REVIEW', label: 'Zur Freigabe' }],
  REVIEW: [
    { target: 'APPROVED', label: 'Freigeben' },
    { target: 'DRAFT', label: 'Zurück an Entwurf' },
  ],
  APPROVED: [{ target: 'PUBLISHED', label: 'Als veröffentlicht markieren' }],
  PUBLISHED: [{ target: 'ARCHIVED', label: 'Archivieren' }],
};

const STATUS_TONES: Record<MediaPostStatus, 'neutral' | 'info' | 'warning' | 'success' | 'brand'> = {
  IDEA: 'neutral',
  DRAFT: 'info',
  REVIEW: 'warning',
  APPROVED: 'success',
  PUBLISHED: 'brand',
  ARCHIVED: 'neutral',
};

export function MediaPage(): JSX.Element {
  const scope = useScope();
  const { can } = useAuth();
  const toast = useToast();
  // Ziele aus Suche und Heute: /medien/beitraege/<id> und /medien?asset=<id>.
  const { id: postId } = useParams();
  const [params] = useSearchParams();
  const assetId = params.get('asset');
  const [tab, setTab] = useState(assetId && !postId ? 'assets' : 'posts');
  useEffect(() => {
    if (postId) setTab('posts');
    else if (assetId) setTab('assets');
  }, [postId, assetId]);
  const [postDialog, setPostDialog] = useState(false);
  const [assetDialog, setAssetDialog] = useState(false);
  const [editingAsset, setEditingAsset] = useState<MediaAssetDto | null>(null);

  const query = scopeQuery(scope);

  // Jede Ansicht blaettert fuer sich - sonst springt der Beitragsteil auf
  // Seite 3, weil man in der Mediathek geblaettert hat.
  const [postPage, setPostPage] = useState(1);
  const [assetPage, setAssetPage] = useState(1);
  useEffect(() => {
    setPostPage(1);
    setAssetPage(1);
  }, [JSON.stringify(query)]);

  const postSeite = useQuery({
    queryKey: ['media', 'posts', query, postPage],
    queryFn: () => api.get<Paged<MediaPostDto>>('/api/media/posts', { ...query, page: postPage }),
  });
  const { isLoading, error } = postSeite;
  const posts = postSeite.data?.items;

  const assetSeite = useQuery({
    queryKey: ['media', 'assets', query, assetPage],
    queryFn: () => api.get<Paged<MediaAssetDto>>('/api/media/assets', { ...query, page: assetPage }),
  });
  const assetsLoading = assetSeite.isLoading;
  const assets = assetSeite.data?.items;

  // Der Beitrag wird einzeln geladen: er liegt evtl. nicht auf der aktuellen Seite oder im Bereich.
  const einzelnerBeitrag = useQuery({
    queryKey: ['media', 'post', postId],
    queryFn: () => api.get<MediaPostDto>(`/api/media/posts/${postId}`),
    enabled: Boolean(postId),
    retry: false,
  });
  const beitragInListe = Boolean(postId && posts?.some((post) => post.id === postId));

  // Medium: hinscrollen oder ehrlich sagen, dass es in der Auswahl fehlt.
  useEffect(() => {
    if (!assetId || tab !== 'assets' || !assets) return;
    const el = document.getElementById(`medium-${assetId}`);
    if (el) el.scrollIntoView({ block: 'center' });
    else toast.show('Das Medium liegt nicht in der aktuellen Auswahl – wechsle den Bereich oder blättere weiter.', 'error');
  }, [assetId, tab, assets]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Medien"
        description="Beiträge, Freigaben und Mediathek"
        actions={
          can('media', 'create') && (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setEditingAsset(null);
                  setAssetDialog(true);
                }}
              >
                + Datei
              </Button>
              <Button onClick={() => setPostDialog(true)}>+ Beitrag</Button>
            </>
          )
        }
      />

      <Tabs
        tabs={[
          // Gesamtzahl, nicht die der angezeigten Seite.
          { id: 'posts', label: 'Planung', badge: postSeite.data?.total },
          { id: 'assets', label: 'Mediathek', badge: assetSeite.data?.total },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'posts' && (
        <>
          {postId && einzelnerBeitrag.error && <ErrorBlock error={einzelnerBeitrag.error} />}
          {postId && einzelnerBeitrag.data && !beitragInListe && (
            <ul className="space-y-2">
              <MediaPostCard post={einzelnerBeitrag.data} highlighted />
            </ul>
          )}
          {isLoading && <LoadingBlock />}
          {error && <ErrorBlock error={error} />}
          {posts && posts.length === 0 && (
            <ScopedEmptyState
              title="Keine Medienbeiträge geplant"
              description="Beiträge werden hier geplant, freigegeben und dokumentiert."
              createAction={
                can('media', 'create') ? (
                  <Button onClick={() => setPostDialog(true)}>+ Beitrag</Button>
                ) : undefined
              }
            />
          )}
          <ul className="space-y-2">
            {(posts ?? []).map((post) => (
              <MediaPostCard key={post.id} post={post} highlighted={post.id === postId} />
            ))}
          </ul>
          {postSeite.data && (
            <Pagination
              page={postSeite.data.page}
              pageSize={postSeite.data.pageSize}
              total={postSeite.data.total}
              onPageChange={setPostPage}
            />
          )}
        </>
      )}

      {tab === 'assets' && (
        <>
          {assetsLoading && <LoadingBlock />}
          {assets && assets.length === 0 && (
            <ScopedEmptyState
              title="Mediathek ist leer"
              createAction={
                can('media', 'create') ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setEditingAsset(null);
                      setAssetDialog(true);
                    }}
                  >
                    + Datei
                  </Button>
                ) : undefined
              }
            />
          )}
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {(assets ?? []).map((asset) => (
              <li key={asset.id} id={`medium-${asset.id}`}>
                <MediaAssetCard
                  highlighted={asset.id === assetId}
                  asset={asset}
                  onEdit={() => {
                    setEditingAsset(asset);
                    setAssetDialog(true);
                  }}
                />
              </li>
            ))}
          </ul>
          {assetSeite.data && (
            <Pagination
              page={assetSeite.data.page}
              pageSize={assetSeite.data.pageSize}
              total={assetSeite.data.total}
              onPageChange={setAssetPage}
            />
          )}
        </>
      )}

      <PostDialog open={postDialog} onClose={() => setPostDialog(false)} />
      <AssetDialog
        open={assetDialog}
        asset={editingAsset}
        onClose={() => {
          setAssetDialog(false);
          setEditingAsset(null);
        }}
      />
    </div>
  );
}

function MediaAssetCard({
  asset,
  onEdit,
  highlighted = false,
}: {
  asset: MediaAssetDto;
  onEdit: () => void;
  highlighted?: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const remove = useMutation({
    mutationFn: () => api.del(`/api/media/assets/${asset.id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['media'] });
      toast.show('Medium gelöscht');
    },
    onError: (error: unknown) =>
      toast.show(error instanceof ApiError ? error.message : 'Löschen fehlgeschlagen', 'error'),
  });

  return (
    <Card className={cn('h-full', highlighted && 'ring-2 ring-brand-500')}>
      <CardBody className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <p className="min-w-0 flex-1 font-medium text-slate-900">{asset.title}</p>
          <Badge tone="neutral">{MEDIA_KIND_LABELS[asset.kind] ?? asset.kind}</Badge>
        </div>
        <p className="text-xs text-slate-500">
          {asset.organisationUnits.map((unit) => unit.name).join(', ')}
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          {asset.downloadUrl && (
            <a
              href={asset.downloadUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex min-h-11 items-center text-sm font-medium text-brand-700 hover:underline sm:min-h-0"
            >
              Öffnen
            </a>
          )}
          {asset.canUpdate && (
            <Button variant="secondary" size="sm" onClick={onEdit}>
              Bearbeiten / Ersetzen
            </Button>
          )}
          {asset.canDelete && (
            <Button
              variant="danger"
              size="sm"
              loading={remove.isPending}
              onClick={() => {
                if (window.confirm(`Medium "${asset.title}" wirklich löschen?`)) remove.mutate();
              }}
            >
              Löschen
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function MediaPostCard({ post, highlighted = false }: { post: MediaPostDto; highlighted?: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();

  const transition = useMutation({
    mutationFn: (status: MediaPostStatus) =>
      api.post<MediaPostDto>(`/api/media/posts/${post.id}/transition`, { status }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['media'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.show('Status geändert');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Änderung fehlgeschlagen', 'error'),
  });

  return (
    <li ref={highlighted ? (el) => el?.scrollIntoView({ block: 'center' }) : undefined}>
      <Card className={cn(highlighted && 'ring-2 ring-brand-500')}>
        <CardBody className="space-y-2">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-medium text-slate-900">{post.title}</p>
              <p className="text-xs text-slate-500">
                {post.organisationUnits.map((u) => u.name).join(', ')}
                {post.plannedAt ? ` · geplant für ${formatDate(post.plannedAt)}` : ''}
              </p>
            </div>
            <Badge tone={STATUS_TONES[post.status]}>{MEDIA_STATUS_LABELS[post.status]}</Badge>
          </div>

          {post.body && <p className="text-sm text-slate-700">{post.body}</p>}

          <div className="flex flex-wrap gap-1">
            {post.channels.map((channel) => (
              <Badge key={channel} tone="info">
                {MEDIA_CHANNEL_LABELS[channel] ?? channel}
              </Badge>
            ))}
          </div>

          <p className="text-xs text-slate-500">
            {post.responsibleName ? `Verantwortlich: ${post.responsibleName}` : 'Ohne Verantwortliche'}
            {post.approverName ? ` · Freigabe: ${post.approverName}` : ''}
          </p>

          <div className="flex flex-wrap gap-2 pt-1">
            {(NEXT_STATUS[post.status] ?? [])
              .filter((next) => next.target === 'APPROVED' ? post.canApprove : post.canUpdate)
              .map((next) => (
              <Button
                key={next.target}
                variant="secondary"
                size="sm"
                onClick={() => transition.mutate(next.target)}
                loading={transition.isPending}
              >
                {next.label}
              </Button>
            ))}
          </div>
        </CardBody>
      </Card>
    </li>
  );
}

function PostDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const scope = useScope();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [assetSearch, setAssetSearch] = useState('');
  const [plannedAt, setPlannedAt] = useState(toDateInput(new Date().toISOString()));
  const [channels, setChannels] = useState<string[]>(['INSTAGRAM']);
  const [responsible, setResponsible] = useState('');
  const [approver, setApprover] = useState('');
  const [assetIds, setAssetIds] = useState<string[]>([]);
  const [unitIds, setUnitIds] = useState<string[]>(scope.unitId ? [scope.unitId] : []);
  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * Dateien fuer den Waehler.
   *
   * Gesucht wird auf dem Server: eine feste Obergrenze wuerde bei einer
   * gewachsenen Mediathek stillschweigend Dateien verschlucken. Angezeigt
   * wird nur ein Ausschnitt - wer mehr braucht, sucht genauer.
   */
  const treffer = useQuery({
    queryKey: ['media', 'assets', 'waehler', scopeQuery(scope), assetSearch],
    queryFn: () =>
      api.get<Paged<MediaAssetDto>>('/api/media/assets', {
        ...scopeQuery(scope),
        ...(assetSearch.trim() ? { search: assetSearch.trim() } : {}),
        pageSize: 25,
      }),
    enabled: open,
  });

  /**
   * Titel einmal gesehener Dateien merken.
   *
   * Sonst verschwaende eine Suche die bereits angehaengten Dateien aus der
   * Liste - man saehe nicht mehr, was am Beitrag haengt, und koennte es auch
   * nicht mehr abwaehlen.
   */
  const [bekannt, setBekannt] = useState<Record<string, string>>({});
  useEffect(() => {
    const gefunden = treffer.data?.items;
    if (!gefunden?.length) return;
    setBekannt((vorher) => {
      const neu = { ...vorher };
      let geaendert = false;
      for (const asset of gefunden) {
        if (neu[asset.id] !== asset.title) {
          neu[asset.id] = asset.title;
          geaendert = true;
        }
      }
      return geaendert ? neu : vorher;
    });
  }, [treffer.data]);

  const gewaehlt = assetIds.map((id) => ({ id, title: bekannt[id] ?? 'Datei' }));
  const uebrige = (treffer.data?.items ?? []).filter((a) => !assetIds.includes(a.id));

  const save = useMutation({
    mutationFn: () =>
      api.post<MediaPostDto>('/api/media/posts', {
        title,
        body: body || null,
        status: 'DRAFT',
        plannedAt: fromDateInput(plannedAt),
        responsiblePersonId: responsible || null,
        approverPersonId: approver || null,
        channels,
        organisationUnitIds: unitIds,
        assetIds,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['media'] });
      toast.show('Medienbeitrag angelegt');
      setTitle('');
      setBody('');
      setAssetIds([]);
      setAssetSearch('');
      onClose();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setErrors(err.fieldErrors());
        toast.show(err.message, 'error');
      }
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Neuer Medienbeitrag"
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={!title || unitIds.length === 0}
          >
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Titel" required error={errors['title']}>
          {(id) => (
            <Input
              id={id}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z. B. Matchankündigung"
            />
          )}
        </Field>

        <Field label="Text">
          {(id) => (
            <Textarea id={id} rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Geplant für">
            {(id) => (
              <Input
                id={id}
                type="date"
                value={plannedAt}
                onChange={(e) => setPlannedAt(e.target.value)}
              />
            )}
          </Field>
          <Field label="Kanäle">
            {() => (
              <div className="space-y-1 rounded-lg border border-slate-300 p-2">
                {Object.entries(MEDIA_CHANNEL_LABELS).map(([value, label]) => (
                  <Checkbox
                    key={value}
                    label={label}
                    checked={channels.includes(value)}
                    onChange={(checked) =>
                      setChannels(
                        checked ? [...channels, value] : channels.filter((c) => c !== value),
                      )
                    }
                  />
                ))}
              </div>
            )}
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Verantwortlich" hint="Leer lassen, wenn niemand zuständig ist">
            {(id) => (
              <PersonSelect
                id={id}
                value={responsible || null}
                onChange={(v) => setResponsible(v ?? '')}
              />
            )}
          </Field>
          <Field label="Freigabe durch" hint="Leer lassen, wenn keine Freigabe nötig ist">
            {(id) => (
              <PersonSelect id={id} value={approver || null} onChange={(v) => setApprover(v ?? '')} />
            )}
          </Field>
        </div>

        <Field label="Bereiche" required error={errors['organisationUnitIds']}>
          {() => <OrgUnitPicker value={unitIds} onChange={setUnitIds} />}
        </Field>

        <Field
          label="Dateien aus der Mediathek"
          hint={
            treffer.data && treffer.data.total > treffer.data.items.length
              ? `${treffer.data.items.length} von ${treffer.data.total} – zum Eingrenzen suchen`
              : undefined
          }
        >
          {(id) => (
            <div className="space-y-2">
              <Input
                id={id}
                value={assetSearch}
                onChange={(e) => setAssetSearch(e.target.value)}
                placeholder="Datei suchen …"
              />
              <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-300 p-2">
                {/* Angehängte Dateien stehen oben und bleiben sichtbar, auch
                    wenn sie nicht zur Suche passen - sonst ließen sie sich
                    nicht mehr abwählen. */}
                {gewaehlt.map((asset) => (
                  <Checkbox
                    key={asset.id}
                    label={asset.title}
                    checked
                    onChange={() => setAssetIds(assetIds.filter((a) => a !== asset.id))}
                  />
                ))}
                {gewaehlt.length > 0 && uebrige.length > 0 && (
                  <hr className="my-1 border-slate-200" />
                )}
                {uebrige.map((asset) => (
                  <Checkbox
                    key={asset.id}
                    label={asset.title}
                    checked={false}
                    onChange={() => setAssetIds([...assetIds, asset.id])}
                  />
                ))}
                {gewaehlt.length === 0 && uebrige.length === 0 && (
                  <p className="p-1 text-sm text-slate-500">
                    {assetSearch.trim() ? 'Keine Datei gefunden.' : 'Die Mediathek ist leer.'}
                  </p>
                )}
              </div>
            </div>
          )}
        </Field>
      </div>
    </Dialog>
  );
}

function AssetDialog({
  open,
  asset,
  onClose,
}: {
  open: boolean;
  asset: MediaAssetDto | null;
  onClose: () => void;
}): JSX.Element {
  const scope = useScope();
  const queryClient = useQueryClient();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [mode, setMode] = useState<'link' | 'file'>('link');
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState('PHOTO');
  const [externalUrl, setExternalUrl] = useState('');
  const [unitIds, setUnitIds] = useState<string[]>(scope.unitId ? [scope.unitId] : []);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (!open) return;
    setMode(asset?.storageProvider === 'LOCAL' ? 'file' : 'link');
    setTitle(asset?.title ?? '');
    setKind(asset?.kind ?? 'PHOTO');
    setExternalUrl(asset?.externalUrl ?? '');
    setUnitIds(asset ? asset.organisationUnits.map((unit) => unit.id) : scope.unitId ? [scope.unitId] : []);
    setSelectedFile(null);
    if (fileRef.current) fileRef.current.value = '';
  }, [open, asset, scope.unitId]);

  const save = useMutation({
    mutationFn: async () => {
      if (mode === 'file') {
        if (asset && !selectedFile && asset.storageProvider === 'LOCAL') {
          return api.patch<MediaAssetDto>(`/api/media/assets/${asset.id}`, {
            title,
            kind,
            organisationUnitIds: unitIds,
            tags: asset.tags,
          });
        }
        if (!selectedFile) throw new ApiError(422, null, 'Bitte eine Datei auswählen');
        const form = new FormData();
        form.append('title', title || selectedFile.name);
        form.append('kind', kind);
        form.append('organisationUnitIds', unitIds.join(','));
        form.append('tags', asset?.tags.join(',') ?? '');
        form.append('file', selectedFile);
        return api.upload<MediaAssetDto>(
          asset ? `/api/media/assets/${asset.id}/replace` : '/api/media/assets/upload',
          form,
        );
      }
      if (!externalUrl.trim()) throw new ApiError(422, null, 'Bitte einen Link angeben');
      if (asset) {
        return api.patch<MediaAssetDto>(`/api/media/assets/${asset.id}`, {
          title,
          kind,
          storageProvider: 'EXTERNAL_LINK',
          externalUrl,
          organisationUnitIds: unitIds,
          tags: asset.tags,
        });
      }
      return api.post<MediaAssetDto>('/api/media/assets', {
        title,
        kind,
        storageProvider: 'EXTERNAL_LINK',
        externalUrl,
        organisationUnitIds: unitIds,
        tags: [],
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['media'] });
      toast.show(asset ? 'Medium aktualisiert' : 'Datei gespeichert');
      setTitle('');
      setExternalUrl('');
      setSelectedFile(null);
      if (fileRef.current) fileRef.current.value = '';
      onClose();
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen', 'error'),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={asset ? 'Medium bearbeiten oder ersetzen' : 'Datei zur Mediathek hinzufügen'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={
              !title.trim() ||
              unitIds.length === 0 ||
              (mode === 'link' && !externalUrl.trim()) ||
              (mode === 'file' && !selectedFile && asset?.storageProvider !== 'LOCAL')
            }
          >
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex gap-2">
          <Button
            variant={mode === 'link' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setMode('link')}
          >
            Externer Link
          </Button>
          <Button
            variant={mode === 'file' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setMode('file')}
          >
            Datei hochladen
          </Button>
        </div>

        <Field label="Titel" required>
          {(id) => <Input id={id} value={title} onChange={(e) => setTitle(e.target.value)} />}
        </Field>

        {mode === 'link' ? (
          <Field label="Link" required>
            {(id) => (
              <Input
                id={id}
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                placeholder="https://…"
              />
            )}
          </Field>
        ) : (
          <Field
            label={asset?.storageProvider === 'LOCAL' ? 'Neue Datei' : 'Datei'}
            required={asset?.storageProvider !== 'LOCAL'}
            hint={asset?.storageProvider === 'LOCAL' ? 'Optional – ohne Auswahl werden nur die Metadaten geändert' : undefined}
          >
            {(id) => (
              <input
                id={id}
                ref={fileRef}
                type="file"
                className="input py-1.5"
                onChange={(event) => setSelectedFile(event.target.files?.[0] ?? null)}
              />
            )}
          </Field>
        )}

        <Field label="Art">
          {(id) => (
            <Select id={id} value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="PHOTO">Foto</option>
              <option value="VIDEO">Video</option>
              <option value="LOGO">Logo</option>
              <option value="GRAPHIC">Grafik</option>
              <option value="TEMPLATE">Vorlage</option>
              <option value="DOCUMENT">Dokument</option>
              <option value="OTHER">Sonstiges</option>
            </Select>
          )}
        </Field>

        <Field label="Bereiche" required>
          {() => <OrgUnitPicker value={unitIds} onChange={setUnitIds} />}
        </Field>
      </div>
    </Dialog>
  );
}
